import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FileText, Download, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(n || 0).toFixed(2)}`;
const day = (iso) => (iso === "collection" ? "on collection" : iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const TONE = { paid: "text-[#10b981] border-[#10b981]/40 bg-[#10b981]/10", overdue: "text-[#ff4f18] border-[#ff4f18]/40 bg-[#ff4f18]/15", collection: "text-[#ffd600] border-[#ffd600]/40 bg-[#ffd600]/10", sent: "text-[#22c3d6] border-[#22c3d6]/40 bg-[#22c3d6]/10", issued: "text-zinc-300 border-zinc-700 bg-zinc-800", cancelled: "text-zinc-500 border-zinc-800" };

// Customer's invoices in their account area, with view + PDF download.
export const MyInvoices = () => {
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["myinvoices"], queryFn: async () => (await api.get("/invoices/mine")).data });
  if (!isLoading && rows.length === 0) return null;
  return (
    <section className="mt-10" data-testid="my-invoices">
      <h2 className="font-display text-xl font-bold text-white mb-4">My invoices</h2>
      <div className="bg-[#12141a] border border-zinc-800 rounded-2xl divide-y divide-zinc-800/70">
        {isLoading ? <p className="p-5 text-zinc-500 font-mono-p text-sm">Loading…</p> : rows.map((r) => (
          <div key={r.number} className="p-4 flex flex-wrap items-center gap-3" data-testid={`my-invoice-${r.number}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="font-mono-p text-white">{r.number} <span className="text-zinc-500 text-xs">· order {r.order_number}</span></p>
              <p className="text-xs text-zinc-400 mt-0.5">Issued {day(r.issued_at)}{r.due > 0 && r.due_at ? ` · due ${day(r.due_at)}` : ""}</p>
            </div>
            <div className="text-right">
              <p className="font-mono-p text-white">{money(r.total)}</p>
              {r.due > 0 && <p className="text-xs text-[#ffd600] font-mono-p">{money(r.due)} due</p>}
            </div>
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${TONE[r.status] || TONE.issued}`} data-testid={`my-invoice-status-${r.number}`}>{r.status === "overdue" && <AlertTriangle size={10} />}{r.status === "collection" ? "due on collection" : r.status}</span>
            <div className="flex gap-1.5">
              <Link to={r.link} data-testid={`my-invoice-view-${r.number}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 border border-zinc-700 text-zinc-200 hover:border-white"><FileText size={11} />View</Link>
              <a href={`${BACKEND}${r.pdf}?download=1`} data-testid={`my-invoice-pdf-${r.number}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[#ff6a13] text-white hover:bg-[#ff8a3d]"><Download size={11} />PDF</a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
